import React, { useState } from 'react';
import { DrawerComponent } from 'components/Drawer';

import visaImg from 'assets/card-brands/visa.png';
import mastercardImg from 'assets/card-brands/mastercard.png';
import amexImg from 'assets/card-brands/amex.png';
import eloImg from 'assets/card-brands/elo.png';
import hipercardImg from 'assets/card-brands/hipercard.png';
import discoverImg from 'assets/card-brands/discover.png';
import dinersImg from 'assets/card-brands/diners.png';
import jcbImg from 'assets/card-brands/jcb.png';
import auraImg from 'assets/card-brands/aura.png';
import hiperImg from 'assets/card-brands/hiper.png';
import credzImg from 'assets/card-brands/credz.png';
import cabalImg from 'assets/card-brands/cabal.png';
import banescardImg from 'assets/card-brands/baners.png';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (token: string, brand: string, last4: string) => void;
  accessToken: string | null;
  idToken: string | null;
};

type CardBrand =
  | 'visa'
  | 'mastercard'
  | 'amex'
  | 'elo'
  | 'hipercard'
  | 'aura'
  | 'jcb'
  | 'diners'
  | 'discover'
  | 'hiper'
  | 'credz'
  | 'cabal'
  | 'banescard'
  | null;

type FieldErrors = {
  holderName?: string;
  cardNumber?: string;
  expiry?: string;
  cvv?: string;
};

const BRAND_IMAGES: Partial<Record<NonNullable<CardBrand>, string>> = {
  visa: visaImg,
  mastercard: mastercardImg,
  amex: amexImg,
  elo: eloImg,
  hipercard: hipercardImg,
  discover: discoverImg,
  diners: dinersImg,
  jcb: jcbImg,
  aura: auraImg,
  hiper: hiperImg,
  credz: credzImg,
  cabal: cabalImg,
  banescard: banescardImg,
};

const detectBrand = (number: string): CardBrand => {
  const n = number.replace(/\D/g, '');
  if (/^4/.test(n)) return 'visa';
  if (/^(5[1-5]|2(2[2-9]|[3-6]\d|7[01]))/.test(n)) return 'mastercard';
  if (/^3[47]/.test(n)) return 'amex';
  if (/^(636368|438935|504175|451416|636297|5067|4576|4011)/.test(n)) return 'elo';
  if (/^(606282|3841)/.test(n)) return 'hipercard';
  if (/^(637095|63737423|63743358)/.test(n)) return 'hiper';
  if (/^50/.test(n)) return 'aura';
  if (/^35(2[89]|[3-8]\d)/.test(n)) return 'jcb';
  if (/^3(0[0-5]|[68])/.test(n)) return 'diners';
  if (/^6(011|5\d{2})/.test(n)) return 'discover';
  if (/^(589892|6042|6043)/.test(n)) return 'cabal';
  if (/^603182/.test(n)) return 'banescard';
  if (/^636759/.test(n)) return 'credz';
  return null;
};

const formatCardNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(.{4})/g, '$1 ').trim();
};

const formatExpiry = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length >= 3) return digits.slice(0, 2) + '/' + digits.slice(2);
  return digits;
};

const inputStyle = (hasError: boolean): React.CSSProperties => ({
  width: '100%',
  padding: '12px 14px',
  border: `1px solid ${hasError ? '#c0392b' : '#e0e0e0'}`,
  height: '56px',
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'Manrope, sans-serif',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '-0.4px',
  outline: 'none',
  boxSizing: 'border-box',
  color: '#1a1a1a',
});

const FieldError = ({ message }: { message?: string }) =>
  message ? (
    <span style={{ fontSize: '12px', color: '#c0392b', marginTop: '4px', display: 'block' }}>
      {message}
    </span>
  ) : null;

const LockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C14.2091 2 16 3.79086 16 6V8H17.75C18.9926 8 20 9.00736 20 10.25V19.75C20 20.9926 18.9926 22 17.75 22H6.25C5.00736 22 4 20.9926 4 19.75V10.25C4 9.00736 5.00736 8 6.25 8H8V6C8 3.79086 9.79086 2 12 2ZM17.75 9.5H6.25C5.83579 9.5 5.5 9.83579 5.5 10.25V19.75C5.5 20.1642 5.83579 20.5 6.25 20.5H17.75C18.1642 20.5 18.5 20.1642 18.5 19.75V10.25C18.5 9.83579 18.1642 9.5 17.75 9.5ZM12.0001 13.5C12.8286 13.5 13.5001 14.1716 13.5001 15C13.5001 15.8284 12.8286 16.5 12.0001 16.5C11.1717 16.5 10.5001 15.8284 10.5001 15C10.5001 14.1716 11.1717 13.5 12.0001 13.5ZM12 3.5C10.6193 3.5 9.5 4.61929 9.5 6V8H14.5V6C14.5 4.61929 13.3807 3.5 12 3.5Z" fill="#1A1A1A" />
  </svg>

);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6.5" stroke="#808080" strokeWidth="1.2" />
    <path d="M8 7.5v4" stroke="#808080" strokeWidth="1.3" strokeLinecap="round" />
    <circle cx="8" cy="5.5" r="0.75" fill="#808080" />
  </svg>
);

export const AdicionarCartaoDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [holderName, setHolderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [showCvvTip, setShowCvvTip] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitted, setSubmitted] = useState(false);

  const brand = detectBrand(cardNumber);
  const brandImg = brand ? BRAND_IMAGES[brand] : undefined;

  const validate = (): FieldErrors => {
    const digits = cardNumber.replace(/\D/g, '');
    const [expMonth, expYear] = expiry.split('/');
    const errors: FieldErrors = {};
    if (!holderName.trim()) errors.holderName = 'Preencha o campo corretamente.';
    if (digits.length < 13) errors.cardNumber = 'Preencha o campo corretamente.';
    if (!expMonth || !expYear || expiry.length < 5) errors.expiry = 'Preencha o campo corretamente.';
    if (!cvv || cvv.length < 3) errors.cvv = 'Preencha o campo corretamente.';
    return errors;
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setApiError(null);
    setLoading(true);
    const digits = cardNumber.replace(/\D/g, '');
    const [expMonth, expYear] = expiry.split('/');
    try {
      const pagarmeKey = process.env.REACT_APP_PAGARME_PUBLIC_KEY;
      const tokenRes = await fetch(
        `https://api.pagar.me/core/v5/tokens?appId=${pagarmeKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'card',
            card: {
              number: digits,
              holder_name: holderName,
              exp_month: parseInt(expMonth, 10),
              exp_year: parseInt(expYear, 10),
              cvv,
            },
          }),
        },
      );
      if (!tokenRes.ok) {
        const err = await tokenRes.json().catch(() => ({}));
        throw new Error(err?.message ?? 'Erro ao tokenizar cartao.');
      }
      const tokenData = await tokenRes.json();
      const cardToken: string = tokenData.id;
      const last4 = digits.slice(-4);
      const brandName = detectBrand(digits) ?? 'card';

      onSuccess(cardToken, brandName, last4);
      onClose();
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? e?.message ?? 'Erro ao salvar cartao. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = <T extends keyof FieldErrors>(field: T, value: string) => {
    if (submitted) {
      setFieldErrors(prev => ({ ...prev, [field]: undefined }));
    }
    if (field === 'holderName') setHolderName(value.toUpperCase());
    if (field === 'cardNumber') setCardNumber(formatCardNumber(value));
    if (field === 'expiry') setExpiry(formatExpiry(value));
    if (field === 'cvv') setCvv(value.replace(/\D/g, '').slice(0, 4));
  };

  const handleClose = () => {
    setHolderName('');
    setCardNumber('');
    setExpiry('');
    setCvv('');
    setApiError(null);
    setFieldErrors({});
    setSubmitted(false);
    onClose();
  };

  return (
    <DrawerComponent open={isOpen} size="default" onCompleteClose={handleClose} noHeader className="drawer-full-height">
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', margin: '0 -2rem' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 2rem 16px', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
              <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10l5-5" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '24px', lineHeight: '32px', letterSpacing: '-0.8px', verticalAlign: 'middle' }}>Adicionar cartao</span>
          </div>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#555', lineHeight: 1 }}>x</button>
        </div>

        <div style={{ overflowY: 'auto', padding: '24px 2rem', flex: 1 }}>
          <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 500, fontSize: '16px', lineHeight: '24px', letterSpacing: '-0.4px', verticalAlign: 'middle', color: '#1a1a1a', marginBottom: '20px' }}>Digite os dados do seu cartao de credito:</p>
          {apiError && <p style={{ color: '#c0392b', fontSize: '13px', marginBottom: '14px' }}>{apiError}</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

            <div>
              <input
                style={inputStyle(!!fieldErrors.holderName)}
                placeholder="Nome no cartao"
                value={holderName}
                onChange={e => handleChange('holderName', e.target.value)}
                autoComplete="cc-name"
              />
              <FieldError message={fieldErrors.holderName} />
            </div>

            <div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <input
                  style={inputStyle(!!fieldErrors.cardNumber)}
                  placeholder="Numero do cartao"
                  value={cardNumber}
                  onChange={e => handleChange('cardNumber', e.target.value)}
                  inputMode="numeric"
                  autoComplete="cc-number"
                />
                {brandImg && (
                  <div style={{ alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: '8px', height: '56px' }}>
                    <img src={brandImg} alt={brand ?? ''} style={{ height: '100%', width: 'auto', objectFit: 'contain' }} />
                  </div>
                )}
              </div>
              <FieldError message={fieldErrors.cardNumber} />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <input
                  style={inputStyle(!!fieldErrors.expiry)}
                  placeholder="Data de expiracao"
                  value={expiry}
                  onChange={e => handleChange('expiry', e.target.value)}
                  inputMode="numeric"
                  autoComplete="cc-exp"
                />
                <FieldError message={fieldErrors.expiry} />
              </div>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  style={{ ...inputStyle(!!fieldErrors.cvv), paddingRight: '36px' }}
                  placeholder="CVV"
                  value={cvv}
                  onChange={e => handleChange('cvv', e.target.value)}
                  inputMode="numeric"
                  autoComplete="cc-csc"
                />
                <button onMouseEnter={() => setShowCvvTip(true)} onMouseLeave={() => setShowCvvTip(false)} style={{ position: 'absolute', right: '10px', top: '28px', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
                  <InfoIcon />
                </button>
                {showCvvTip && (
                  <div style={{ position: 'absolute', right: 0, top: '110%', background: '#333', color: '#fff', fontSize: '12px', padding: '6px 10px', borderRadius: '6px', whiteSpace: 'nowrap', zIndex: 10 }}>
                    Codigo de seguranca de 3 digitos no verso do cartao.
                  </div>
                )}
                <FieldError message={fieldErrors.cvv} />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#f8f8f8', borderRadius: '8px', padding: '14px', marginTop: '4px' }}>
              <LockIcon />
              <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 500, fontSize: '16px', lineHeight: '24px', letterSpacing: '-0.4px', verticalAlign: 'middle', color: '#555', margin: 0 }}>Seus dados do cartao sao protegidos e processados com seguranca pela Pagar.me. A Kenlo nao armazena as informacoes.</p>
            </div>

          </div>
        </div>

        <div style={{ borderTop: '1px solid #e0e0e0', padding: '16px 2rem', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={handleSubmit} disabled={loading} style={{ padding: '12px 28px', background: loading ? '#ccc' : '#298EE6', color: '#fff', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '16px' }}>
            {loading ? 'Salvando...' : 'Salvar cartao'}
          </button>
        </div>

      </div>
    </DrawerComponent>
  );
};
